import React, { useState } from 'react';
import { Alert, Button, Card, Checkbox, Col, DatePicker, Row, Space, Typography, message } from 'antd';
import { CloudUploadOutlined, ExportOutlined } from '@ant-design/icons';
import ExportProgress from '../shared/ExportProgress';
import downloadService from '../../services/downloadService';

const { Text, Title } = Typography;
const tables = [
  { type: 'commande', title: 'Commandes achat', files: 'COMACHAT.dbf / DETCOMAC.dbf' },
  { type: 'reception', title: 'Bons de réception', files: 'BONREC.dbf / DETBONREC.dbf' },
  { type: 'facture', title: 'Factures achat', files: 'FACACHAT.dbf / DETFACAC.dbf' },
  { type: 'avoir', title: 'Avoirs achat', files: 'AVRACHAT.dbf / DETAVRAC.dbf' }
];

const DownloadAchatTables = () => {
  const [selections, setSelections] = useState(Object.fromEntries(tables.map(({ type }) => [type, { main: false, detail: false }])));
  const [dateRange, setDateRange] = useState(null);
  const [loading, setLoading] = useState(null);
  const [result, setResult] = useState(null);
  const [progress, setProgress] = useState(null);

  const handleUpload = async (type) => {
    if (loading !== null) return;
    const selection = selections[type];
    if (type !== 'all' && !selection.main && !selection.detail) return;
    setLoading(type);
    setResult(null);
    setProgress({ stage: 'starting', table: 'Tables achat', percent: 0 });
    try {
      const part = type === 'all' || (selection.main && selection.detail) ? 'both' : selection.main ? 'main' : 'detail';
      const response = await downloadService.exportAchatWithProgress({
        onProgress: setProgress,
        type,
        part,
        startDate: dateRange?.[0]?.startOf('day'),
        endDate: dateRange?.[1]?.endOf('day')
      });
      setResult(response);
      setProgress(response);
      if (response.success && response.ftpUpload?.success) message.success('Tables achat exportées vers /celer/achat/table.');
      else message.error(response.message || 'Échec de l’export des tables achat.');
    } catch (error) {
      setProgress(previous => ({ ...previous, stage: 'failed', message: error.message }));
      const failure = error.response?.data;
      setResult(failure || { success: false, message: error.message || 'Échec de l’export des tables achat.' });
      message.error(failure?.message || 'Échec de l’export des tables achat.');
    } finally {
      setLoading(null);
    }
  };

  return (
    <Card className="export-tables-card">
      <Row align="middle" justify="space-between" gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col><Title level={4}>Exportation des tables achat vers le serveur FTP (DBF)</Title></Col>
        <Col>
          <Button type="primary" icon={<ExportOutlined />} loading={loading === 'all'} disabled={loading !== null} onClick={() => handleUpload('all')}>
            Tout exporter — Achat
          </Button>
        </Col>
      </Row>
      <ExportProgress progress={progress} title="Tables achat" />
      <Card title="Période (optionnel)" size="small" style={{ marginBottom: 24 }}>
        <DatePicker.RangePicker
          value={dateRange}
          onChange={setDateRange}
          disabled={loading !== null}
          style={{ width: '100%' }}
          placeholder={['Date de début', 'Date de fin']}
        />
        <Text type="secondary" style={{ display: 'block', marginTop: 8 }}>
          Sans période, toutes les données achat sont exportées. Les dates de début et de fin sont incluses.
        </Text>
        <Text style={{ display: 'block', marginTop: 8 }}>
          Destination FTP : /celer/achat/table. « Tout exporter » inclut les entêtes et détails des quatre tables achat.
        </Text>
      </Card>
      {result && (
        <Alert
          style={{ marginBottom: 24 }}
          showIcon
          type={result.success && result.ftpUpload?.success ? 'success' : 'error'}
          message={result.message}
          description={
            <Space direction="vertical">
              {result.error && <Text>{result.error}</Text>}
              {result.ftpUpload?.uploadedFiles?.map(file => <Text key={file}>Transféré : {file}</Text>)}
              {result.ftpUpload?.failedFiles?.map(({ file, error }) => <Text type="danger" key={file}>Échec : {file} — {error}</Text>)}
            </Space>
          }
        />
      )}
      <Row gutter={[16, 16]}>
        {tables.map(({ type, title, files }) => (
          <Col xs={24} lg={12} key={type}>
            <Card title={title} extra={
              <Button
                type="primary"
                icon={<CloudUploadOutlined />}
                loading={loading === type}
                disabled={loading !== null || (!selections[type].main && !selections[type].detail)}
                onClick={() => handleUpload(type)}
              >Exporter</Button>
            }>
              <Space direction="vertical">
                {['main', 'detail'].map(part => (
                  <Checkbox
                    key={part}
                    checked={selections[type][part]}
                    disabled={loading !== null}
                    onChange={event => {
                      const checked = event.target.checked;
                      setSelections(previous => ({ ...previous, [type]: { ...previous[type], [part]: checked } }));
                    }}
                  >{part === 'main' ? 'Entête' : 'Détails'}</Checkbox>
                ))}
                <Text type="secondary">{files}</Text>
              </Space>
            </Card>
          </Col>
        ))}
      </Row>
    </Card>
  );
};

export default DownloadAchatTables;
